/**
 * Exponential smoothing scorer.
 *
 * Score = weighted average where recent outcomes matter most.
 * alpha = 0.1 means each new outcome contributes 10% to the score.
 * Range is always [0, 1] (displayed as 0-100% in the UI).
 */

import { addDays, compareDates } from "./day-boundary";
import { getDueDatesInRange, getPeriodBounds } from "./schedule";

import type { CompletionOutcome, CompletionRecord, PeriodBounds, ScheduleConfig } from "./types";

export const ALPHA = 0.1;
// Skips apply half the miss penalty to scores. This is the only accountability
// mechanism for skipped days — streaks are preserved. Decided in pre-launch roundtable.
const SKIP_FACTOR = 0.5;

function clamp(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * Single-outcome score update (for daily, specific_days, every_n_days).
 */
export function updateScore(previousScore: number, outcome: CompletionOutcome): number {
  switch (outcome) {
    case "complete":
      return clamp(previousScore * (1 - ALPHA) + ALPHA);
    case "miss":
      return clamp(previousScore * (1 - ALPHA));
    case "skip":
      return clamp(previousScore * (1 - ALPHA * SKIP_FACTOR));
  }
}

/**
 * Period-end score update (for x_per_week, x_per_month).
 *
 * completionRate = min(completed / target, 1)
 * Skipped days within the period get partial decay applied per-skip before the period update.
 */
export function updateScoreForPeriod(
  previousScore: number,
  completedCount: number,
  targetCount: number,
  skippedCount: number,
): number {
  // Apply partial decay for each skip first
  let score = previousScore;
  for (let i = 0; i < skippedCount; i++) {
    score = clamp(score * (1 - ALPHA * SKIP_FACTOR));
  }

  // Then apply the period completion rate
  const completionRate = Math.min(completedCount / targetCount, 1);
  return clamp(score * (1 - ALPHA) + completionRate * ALPHA);
}

/**
 * Compute score by replaying the full completion history from startDate to endDate.
 *
 * For daily/specific_days/every_n_days: evaluates each due date individually.
 * For x_per_week/x_per_month: evaluates at the end of each period.
 */
export function computeScore(
  config: ScheduleConfig,
  completions: CompletionRecord[],
  startDate: string,
  endDate: string,
  firstDayOfWeek: number,
): number {
  // Build a lookup map: effective_date → CompletionRecord[]
  const completionMap = buildCompletionMap(completions);

  if (config.type === "x_per_week" || config.type === "x_per_month") {
    return computePeriodScore(config, completionMap, startDate, endDate, firstDayOfWeek);
  }

  return computeDailyScore(config, completionMap, startDate, endDate);
}

/**
 * Compute score for daily/specific_days/every_n_days schedules.
 * Walks through each due date and applies updateScore.
 */
function computeDailyScore(
  config: ScheduleConfig,
  completionMap: Map<string, CompletionRecord[]>,
  startDate: string,
  endDate: string,
): number {
  const context = { habitStartDate: startDate, completions: flattenMap(completionMap) };
  const dueDates = getDueDatesInRange(config, startDate, endDate, context);

  let score = 0;

  for (const date of dueDates) {
    const outcome = getOutcomeForDate(completionMap, date);
    score = updateScore(score, outcome);
  }

  // Also account for non-due dates that have skip entries
  // (user explicitly skipped a non-due day — unusual but possible)
  // We only process due dates for score calculation per the algorithm spec.

  return score;
}

/**
 * Compute score for x_per_week/x_per_month schedules.
 * Walks through each period and applies updateScoreForPeriod.
 */
function computePeriodScore(
  config: ScheduleConfig & { type: "x_per_week" | "x_per_month" },
  completionMap: Map<string, CompletionRecord[]>,
  startDate: string,
  endDate: string,
  firstDayOfWeek: number,
): number {
  let score = 0;
  const periods = getDistinctPeriods(config, startDate, endDate, firstDayOfWeek);

  for (const period of periods) {
    const { completed, skipped } = countCompletionsInPeriod(completionMap, period);
    score = updateScoreForPeriod(score, completed, config.count, skipped);
  }

  return score;
}

/**
 * Get all distinct periods between startDate and endDate.
 */
export function getDistinctPeriods(
  config: ScheduleConfig & { type: "x_per_week" | "x_per_month" },
  startDate: string,
  endDate: string,
  firstDayOfWeek: number,
): PeriodBounds[] {
  const periods: PeriodBounds[] = [];
  const seen = new Set<string>();
  let current = startDate;

  while (compareDates(current, endDate) <= 0) {
    const bounds = getPeriodBounds(config, current, firstDayOfWeek);
    if (bounds) {
      const key = bounds.start;
      if (!seen.has(key)) {
        seen.add(key);
        // Only include periods that have ended (or are the current period up to endDate)
        periods.push({
          start: compareDates(bounds.start, startDate) < 0 ? startDate : bounds.start,
          end: compareDates(bounds.end, endDate) > 0 ? endDate : bounds.end,
        });
      }
    }
    // Jump to next period start to avoid iterating day-by-day
    if (bounds) {
      current = addDays(bounds.end, 1);
    } else {
      current = addDays(current, 1);
    }
  }

  return periods;
}

/**
 * Count completed and skipped entries within a period.
 */
export function countCompletionsInPeriod(
  completionMap: Map<string, CompletionRecord[]>,
  period: PeriodBounds,
): { completed: number; skipped: number } {
  let completed = 0;
  let skipped = 0;
  let current = period.start;

  while (compareDates(current, period.end) <= 0) {
    const records = completionMap.get(current);
    if (records) {
      for (const r of records) {
        if (r.type === "done" || r.type === "backfill") {
          completed++;
        } else if (r.type === "skip") {
          skipped++;
        }
      }
    }
    current = addDays(current, 1);
  }

  return { completed, skipped };
}

/**
 * Get the completion outcome for a specific date.
 */
export function getOutcomeForDate(
  completionMap: Map<string, CompletionRecord[]>,
  date: string,
): CompletionOutcome {
  const records = completionMap.get(date);
  if (!records || records.length === 0) return "miss";

  // If any record is done/backfill, count as complete
  for (const r of records) {
    if (r.type === "done" || r.type === "backfill") return "complete";
  }

  // If only skips
  for (const r of records) {
    if (r.type === "skip") return "skip";
  }

  return "miss";
}

export function buildCompletionMap(
  completions: CompletionRecord[],
): Map<string, CompletionRecord[]> {
  const map = new Map<string, CompletionRecord[]>();
  for (const c of completions) {
    const existing = map.get(c.effective_date);
    if (existing) {
      existing.push(c);
    } else {
      map.set(c.effective_date, [c]);
    }
  }
  return map;
}

function flattenMap(map: Map<string, CompletionRecord[]>): CompletionRecord[] {
  const result: CompletionRecord[] = [];
  for (const records of map.values()) {
    result.push(...records);
  }
  return result;
}
